import React, { useMemo } from 'react';

export const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export const formatXg = (value) => toNumber(value).toFixed(2);

export const getShotsTotal = (stats) => {
  if (stats?.final_shots_total != null) {
    return toNumber(stats.final_shots_total);
  }

  return (
    toNumber(stats?.final_shots_on) +
    toNumber(stats?.final_shots_off) +
    toNumber(stats?.final_shots_blocked)
  );
};

const isGoalkeeper = (player) => {
  const position = String(player?.position || '').trim().toUpperCase();
  return position === 'GK';
};

export const getSaves = (team) => {
  const players = Array.isArray(team?.players) ? team.players : [];
  const goalkeeper = players.find(isGoalkeeper);

  if (goalkeeper?.stats?.saves != null) {
    return toNumber(goalkeeper.stats.saves);
  }

  return players.reduce((total, player) => total + toNumber(player?.stats?.saves), 0);
};

const getTeamName = (team, fallback) => (
  team?.shortTitle ||
  team?.shortName ||
  team?.name ||
  fallback
);

const getAccessibleTeamName = (team, fallback) => (
  team?.name ||
  team?.shortTitle ||
  team?.shortName ||
  fallback
);

const getShareStyle = (homeValue, awayValue) => {
  const home = Math.max(toNumber(homeValue), 0);
  const away = Math.max(toNumber(awayValue), 0);
  const total = home + away;

  // A 0-0 stat is level, so it reads as two equal halves, not an empty track.
  if (total <= 0) {
    return {
      '--home-share': '50%',
      '--away-share': '50%'
    };
  }

  return {
    '--home-share': `${(home / total) * 100}%`,
    '--away-share': `${(away / total) * 100}%`
  };
};

const makeNumberRow = (id, label, homeValue, awayValue) => ({
  id,
  label,
  homeValue: toNumber(homeValue),
  awayValue: toNumber(awayValue),
  homeShareValue: homeValue,
  awayShareValue: awayValue
});

const hasFiniteValue = (value) => (
  value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
);

/**
 * Possession arrives on the engine's team stats but was never rendered before
 * the redesign. It stays hidden when the engine does not send it.
 */
export const getPossessionSplit = (teamOne, teamTwo) => {
  const home = teamOne?.stats?.possession_percentage;
  const away = teamTwo?.stats?.possession_percentage;

  if (!hasFiniteValue(home) && !hasFiniteValue(away)) return null;

  const homeValue = hasFiniteValue(home) ? Math.round(Number(home)) : null;
  const awayValue = hasFiniteValue(away) ? Math.round(Number(away)) : null;

  if (homeValue !== null && awayValue !== null) {
    return { home: homeValue, away: awayValue };
  }
  if (homeValue !== null) {
    return { home: homeValue, away: Math.max(0, 100 - homeValue) };
  }
  return { home: Math.max(0, 100 - awayValue), away: awayValue };
};

export const buildLiveStatRows = (teamOne, teamTwo) => {
  const teamOneStats = teamOne?.stats || {};
  const teamTwoStats = teamTwo?.stats || {};

  const rows = [
    makeNumberRow('shots', 'Shots', getShotsTotal(teamOneStats), getShotsTotal(teamTwoStats)),
    makeNumberRow('on-target', 'On target', teamOneStats.final_shots_on, teamTwoStats.final_shots_on),
    makeNumberRow('off-target', 'Off target', teamOneStats.final_shots_off, teamTwoStats.final_shots_off),
    makeNumberRow('blocked', 'Blocked', teamOneStats.final_shots_blocked, teamTwoStats.final_shots_blocked),
    makeNumberRow('fouls', 'Fouls', teamOneStats.final_fouls, teamTwoStats.final_fouls)
  ];

  if (hasFiniteValue(teamOneStats.yellow_cards) || hasFiniteValue(teamTwoStats.yellow_cards)) {
    rows.push(makeNumberRow('yellow-cards', 'Yellow cards', teamOneStats.yellow_cards, teamTwoStats.yellow_cards));
  }

  rows.push(
    makeNumberRow('saves', 'Saves', getSaves(teamOne), getSaves(teamTwo)),
    makeNumberRow('tackles', 'Tackles', teamOneStats.successful_tackles, teamTwoStats.successful_tackles),
    makeNumberRow('dribbles', 'Dribbles', teamOneStats.successful_dribbles, teamTwoStats.successful_dribbles),
    makeNumberRow('crosses', 'Crosses', teamOneStats.successful_crosses, teamTwoStats.successful_crosses),
    makeNumberRow('recoveries', 'Recoveries', teamOneStats.ball_recoveries, teamTwoStats.ball_recoveries),
    makeNumberRow('aerial-duels', 'Aerial duels', teamOneStats.aerial_duels_won, teamTwoStats.aerial_duels_won)
  );

  // Corners only exist on newer engine payloads.
  if (hasFiniteValue(teamOneStats.corners) || hasFiniteValue(teamTwoStats.corners)) {
    rows.push(makeNumberRow('corners', 'Corners', teamOneStats.corners, teamTwoStats.corners));
  }

  rows.push(makeNumberRow('substitutions', 'Substitutions', teamOneStats.substitutions_made, teamTwoStats.substitutions_made));

  return rows;
};

/**
 * Normalises `momentum_buckets` into signed values where positive is the home
 * side. Returns null when the engine never sent them so the card can be hidden.
 */
export const buildMomentumSeries = (momentumBuckets) => {
  if (!Array.isArray(momentumBuckets) || momentumBuckets.length === 0) return null;

  const series = momentumBuckets
    .map((bucket) => {
      // Number(null) is 0, so guard the raw value before coercing.
      const home = hasFiniteValue(bucket?.home) ? Number(bucket.home) : null;
      const away = hasFiniteValue(bucket?.away) ? Number(bucket.away) : null;
      if (home === null && away === null) return null;
      const homeValue = home ?? 0;
      const awayValue = away ?? 0;
      const swing = Math.max(-10, Math.min(10, homeValue - awayValue));
      return {
        minute: Number.isFinite(Number(bucket?.minute)) ? Number(bucket.minute) : null,
        swing
      };
    })
    .filter(Boolean);

  return series.length > 0 ? series : null;
};

// The engine samples momentum every five in-game minutes, so a full match
// (with stoppage time) spans fixed slots at minutes 5, 10, ... 95.
export const MOMENTUM_SLOT_MINUTES = Array.from({ length: 19 }, (_, index) => (index + 1) * 5);

const MOMENTUM_MIN_VISIBLE_HEIGHT = 10;
const MOMENTUM_NEUTRAL_SWING = 0.05;

/**
 * Sofascore-style layout: every slot of the match is always on the axis, so
 * bar width never changes as buckets arrive; elapsed slots fill in from the
 * left. Heights are scaled against the strongest swing of the match so far
 * (floored so early noise cannot fill the chart).
 */
export const buildMomentumColumns = (series) => {
  if (!Array.isArray(series) || series.length === 0) return null;

  const swingByMinute = new Map();
  series.forEach((bucket) => {
    if (bucket?.minute != null) swingByMinute.set(bucket.minute, bucket.swing);
  });

  const scale = Math.max(4, ...series.map((bucket) => Math.abs(bucket.swing)));

  return MOMENTUM_SLOT_MINUTES.map((minute) => {
    const swing = swingByMinute.get(minute);
    if (swing === undefined) {
      return { minute, elapsed: false, side: null, heightPercent: 0 };
    }

    const magnitude = Math.abs(swing);
    if (magnitude < MOMENTUM_NEUTRAL_SWING) {
      return { minute, elapsed: true, side: null, heightPercent: 0 };
    }

    return {
      minute,
      elapsed: true,
      side: swing > 0 ? 'home' : 'away',
      heightPercent: Math.max(
        MOMENTUM_MIN_VISIBLE_HEIGHT,
        Math.round((magnitude / scale) * 100)
      )
    };
  });
};

function StatMeter({ homeShareValue, awayShareValue }) {
  return (
    <span
      className="mx-statrow__meter"
      style={getShareStyle(homeShareValue, awayShareValue)}
      aria-hidden="true"
    >
      <span className="mx-statrow__meter-fill mx-statrow__meter-fill--home" />
      <span className="mx-statrow__meter-fill mx-statrow__meter-fill--away" />
    </span>
  );
}

/**
 * Stats tab content: possession, expected goals, the full stat meter table and
 * the momentum chart. Everything degrades to hidden when the engine has not
 * sent that particular field.
 */
function LiveMatchStats({ teamOne, teamTwo, momentumBuckets }) {
  const rows = useMemo(() => buildLiveStatRows(teamOne, teamTwo), [teamOne, teamTwo]);
  const possession = useMemo(() => getPossessionSplit(teamOne, teamTwo), [teamOne, teamTwo]);
  const momentum = useMemo(
    () => buildMomentumColumns(buildMomentumSeries(momentumBuckets)),
    [momentumBuckets]
  );

  const teamOneName = getTeamName(teamOne, 'Home');
  const teamTwoName = getTeamName(teamTwo, 'Away');
  const teamOneAccessibleName = getAccessibleTeamName(teamOne, 'Home team');
  const teamTwoAccessibleName = getAccessibleTeamName(teamTwo, 'Away team');

  const homeXg = formatXg(teamOne?.stats?.xg_total);
  const awayXg = formatXg(teamTwo?.stats?.xg_total);

  return (
    <section className="live-match-stats" aria-label="Live match stats" data-testid="live-match-stats">
      {possession && (
        <div className="mx-card" data-testid="match-possession">
          <h5>Possession</h5>
          <div className="mx-poss">
            <div className="mx-poss__vals">
              <span>{possession.home}%</span>
              <span>{possession.away}%</span>
            </div>
            <div className="mx-poss__bar" aria-hidden="true">
              <span className="mx-poss__home" style={{ width: `${possession.home}%` }} />
              <span className="mx-poss__away" />
            </div>
          </div>
        </div>
      )}

      <div className="mx-card">
        <h5>Expected goals (xG)</h5>
        <div className="mx-xg">
          <div className="mx-xg__box mx-xg__box--home">
            <div
              className="mx-xg__value"
              data-testid="live-stat-xg-home"
              aria-label={`${teamOneAccessibleName} expected goals: ${homeXg}`}
            >
              {homeXg}
            </div>
            <div className="mx-xg__label">{teamOneName}</div>
          </div>
          <div className="mx-xg__box mx-xg__box--away">
            <div
              className="mx-xg__value"
              data-testid="live-stat-xg-away"
              aria-label={`${teamTwoAccessibleName} expected goals: ${awayXg}`}
            >
              {awayXg}
            </div>
            <div className="mx-xg__label">{teamTwoName}</div>
          </div>
        </div>
      </div>

      <div className="mx-card">
        <div className="live-match-stats__top">
          <span className="live-match-stats__eyebrow">Match</span>
          <h2 className="live-match-stats__title">Match stats</h2>
        </div>

        <div
          className="live-match-stats__table"
          role="table"
          aria-label={`${teamOneAccessibleName} versus ${teamTwoAccessibleName} live match stats`}
        >
          <div className="live-match-stats__head" role="rowgroup">
            <div className="live-match-stats__teams" role="row">
              <span
                className="live-match-stats__team live-match-stats__team--home"
                role="columnheader"
                title={teamOneName}
                aria-label={teamOneAccessibleName}
              >
                {teamOneName}
              </span>
              <span className="live-match-stats__versus" role="columnheader" aria-label="Stat">
                vs
              </span>
              <span
                className="live-match-stats__team live-match-stats__team--away"
                role="columnheader"
                title={teamTwoName}
                aria-label={teamTwoAccessibleName}
              >
                {teamTwoName}
              </span>
            </div>
          </div>

          <div className="live-match-stats__rows" role="rowgroup">
            {rows.map((row) => {
              // On a tie both sides "lead", so level stats light up evenly.
              const homeLeads = row.homeValue >= row.awayValue;
              const awayLeads = row.awayValue >= row.homeValue;
              return (
                <div className="mx-statrow" data-testid={`live-stat-row-${row.id}`} key={row.id} role="row">
                  <span
                    className={`mx-statrow__value${homeLeads ? ' is-leading' : ''}`}
                    data-testid={`live-stat-${row.id}-home`}
                    role="cell"
                    aria-label={`${teamOneAccessibleName} ${row.label}: ${row.homeValue}`}
                  >
                    {row.homeValue}
                  </span>
                  <span className="mx-statrow__mid">
                    <span className="mx-statrow__label" role="rowheader">
                      {row.label}
                    </span>
                    <StatMeter homeShareValue={row.homeShareValue} awayShareValue={row.awayShareValue} />
                  </span>
                  <span
                    className={`mx-statrow__value${awayLeads ? ' is-leading' : ''}`}
                    data-testid={`live-stat-${row.id}-away`}
                    role="cell"
                    aria-label={`${teamTwoAccessibleName} ${row.label}: ${row.awayValue}`}
                  >
                    {row.awayValue}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {momentum && (
        <div className="mx-card" data-testid="match-momentum">
          <h5>
            Momentum
            <span className="mx-newtag">NEW</span>
          </h5>
          <div className="mx-momentum">
            <div className="mx-momentum__chart">
              {momentum.map((column) => (
                <span
                  className={`mx-momentum__col${column.elapsed ? ' is-elapsed' : ''}`}
                  key={column.minute}
                >
                  <span className="mx-momentum__half mx-momentum__half--home">
                    {column.side === 'home' && <i style={{ height: `${column.heightPercent}%` }} />}
                  </span>
                  <span className="mx-momentum__half mx-momentum__half--away">
                    {column.side === 'away' && <i style={{ height: `${column.heightPercent}%` }} />}
                  </span>
                </span>
              ))}
            </div>
            <div className="mx-momentum__labels">
              <span>0&apos;</span>
              <span>HT</span>
              <span>90&apos;</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default LiveMatchStats;
